import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

/**
 * Slice de sessão do risco (estado client-side específico do tipo). O estado de drawer (qual tipo/payload
 * está aberto) mora no store genérico `useNotificationDrawerStore`; aqui ficam só os bits do risco:
 *
 * - `dismissedBucketEnd`: persistido (localStorage) pra o dispensar sobreviver reload — lógica do #11194.
 *   Complementa o silence endpoint (~2h server-side): o persist some imediatamente na sessão; o silence
 *   suprime no servidor pelas próximas janelas. Sem persist, um reload reexibiria o bucket dispensado.
 * - `lastDisplayedBucketEnd`: dispara o evento "Displayed" uma vez por bucket. In-memory.
 */
interface RiskRecognitionSliceState {
  lastDisplayedBucketEnd: string | null;
  dismissedBucketEnd: string | null;
  markDisplayed: (bucketEnd: string) => void;
  // O highlight vem do endpoint de highlights, então os callers passam o bucket end acionado.
  dismiss: (bucketEnd: string | null) => void;
  reset: () => void;
}

type PersistedState = Pick<RiskRecognitionSliceState, 'dismissedBucketEnd'>;

const initialState = { lastDisplayedBucketEnd: null, dismissedBucketEnd: null } as const;

const useRiskRecognitionSlice = create<RiskRecognitionSliceState, [['zustand/devtools', never], ['zustand/persist', PersistedState]]>(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        markDisplayed: (bucketEnd) => set({ lastDisplayedBucketEnd: bucketEnd }, false, 'markDisplayed'),
        dismiss: (bucketEnd) => set({ dismissedBucketEnd: bucketEnd }, false, 'dismiss'),
        reset: () => set({ ...initialState }, false, 'reset'),
      }),
      {
        name: 'risk-recognition',
        version: 1,
        storage: createJSONStorage(() => localStorage),
        // Só o bucket dispensado sobrevive reload; o resto é estado de sessão.
        partialize: (state) => ({ dismissedBucketEnd: state.dismissedBucketEnd }),
      }
    ),
    { name: 'useRiskRecognitionSlice' }
  )
);

export default useRiskRecognitionSlice;
